import { useCallback, useState } from "react";
import { signOut, useSession } from "next-auth/react";
import {
  LogOut,
  User,
  Settings,
  HelpCircle,
  Shield,
  Bell,
  Building2,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { Button, Menu, MenuSelectionDetails, Portal } from "@chakra-ui/react";
import { useOrganization } from "@/lib/hooks/use-organization";
import styles from "./user-dropdown.module.css";

export default function UserDropdown() {
  const { data: session } = useSession();
  const { email, image } = session?.user || {};
  const { organization } = useOrganization();
  const [imageError, setImageError] = useState(false);

  const getAvatarUrl = useCallback(() => {
    if (image && !imageError) {
      return image;
    }
    return `https://avatars.dicebear.com/api/micah/${email || "default"}.svg`;
  }, [image, imageError, email]);

  const handleLogout = async () => {
    await fetch("/api/logout", {
      method: "POST",
    });
    signOut({ redirect: false });
  };

  const onMenuItemSelected = useCallback((details: MenuSelectionDetails) => {
    const { value } = details;
    if (value === "logout") {
      handleLogout();
    }
  }, []);

  if (!email) return null;

  const menuItems = [
    {
      href: "/settings/account",
      value: "profile",
      title: "View Profile",
      icon: <User className='h-4 w-4' />,
    },
    {
      href: "/settings/preferences",
      value: "preferences",
      title: "Preferences",
      icon: <Settings className='h-4 w-4' />,
    },
    {
      href: "/settings/notifications",
      value: "notifications",
      title: "Notifications",
      icon: <Bell className='h-4 w-4' />,
    },
    {
      href: "/settings/security",
      value: "security",
      title: "Security & Privacy",
      icon: <Shield className='h-4 w-4' />,
    },
    {
      href: "#",
      value: "switch-org",
      title: "Switch Organization",
      icon: <Building2 className='h-4 w-4' />,
      disabled: true,
    },
    {
      href: "/help",
      value: "help",
      title: "Help & Support",
      icon: <HelpCircle className='h-4 w-4' />,
    },
  ];

  return (
    <Menu.Root onSelect={onMenuItemSelected}>
      <Menu.Trigger asChild>
        <Button variant='plain' size='sm'>
          <div className={styles.avatarContainer}>
            <Image
              alt={email || "User avatar"}
              src={getAvatarUrl()}
              width={40}
              height={40}
              onError={() => {
                setImageError(true);
              }}
            />
          </div>
        </Button>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            <div className={styles.userInfo}>
              <p className={styles.userEmail}>
                {email} <span className={styles.userRole}>|</span>{" "}
                {organization?.userOrgRole?.toLowerCase().replace(/_/g, " ")}
              </p>
              {organization?.name && (
                <p className={styles.organizationName}>{organization.name}</p>
              )}
            </div>
            {menuItems.map((item) => (
              <Link href={item.href} passHref key={item.value}>
                <Menu.Item
                  as='a'
                  value={item.value}
                  disabled={item.disabled}
                  title={
                    item.disabled
                      ? "Coming soon - Multiple organization support"
                      : undefined
                  }
                >
                  {item.icon}
                  <p className={styles.menuItem}>{item.title}</p>
                </Menu.Item>
              </Link>
            ))}
            <Menu.Item value='logout'>
              <LogOut className='h-4 w-4' />
              <p className={styles.menuItem}>Logout</p>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
